import React from 'react';

import { StackContext } from './StackContext';

export type StackProviderProps = React.PropsWithChildren;

export const StackProvider: React.FC<StackProviderProps> = ({ children }) => {
    const [stacks, setStacks] = React.useState<Record<string, string[]>>({});
    const stacksRef = React.useRef<typeof stacks>(stacks);

    const updateStack = React.useCallback((key: string, newStack: string[]) => {
        setStacks({ ...stacksRef.current, [key]: newStack });
        stacksRef.current[key] = newStack;
    }, []);

    const toggle = React.useCallback(
        (key: string, id: string, active: boolean) => {
            const oldStack = stacksRef.current[key] ?? [];
            const isInStack = oldStack.includes(id);
            if (active && !isInStack) {
                updateStack(key, [...oldStack, id]);
            } else if (!active && isInStack) {
                updateStack(
                    key,
                    oldStack.filter((item) => item !== id),
                );
            }
        },
        [updateStack],
    );

    const get = React.useCallback((key: string) => stacksRef.current[key] ?? [], []);

    const context = React.useMemo(
        () => ({
            stacks,
            toggle,
            get,
        }),
        [stacks, toggle, get],
    );

    return <StackContext.Provider value={context}>{children}</StackContext.Provider>;
};

StackProvider.displayName = 'StackProvider';
